import React from 'react';
import { motion, AnimatePresence, type Transition } from 'framer-motion';
import {
  modalOverlayVariants,
  modalContentVariants,
  modalTransition,
} from '../../utils/motionVariants';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * Reusable animated modal with overlay fade + content scale/fade.
 */

interface AnimatedModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Called when overlay is clicked */
  onClose: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Additional class for the modal content wrapper */
  className?: string;
  /** Additional class for the overlay */
  overlayClassName?: string;
  /** Inline styles for the content wrapper */
  style?: React.CSSProperties;
}

export function AnimatedModal({
  isOpen,
  onClose,
  children,
  className = '',
  overlayClassName = '',
  style,
}: AnimatedModalProps): React.ReactElement {
  const reducedMotion = useReducedMotion();
  const transition: Transition = reducedMotion ? { duration: 0 } : modalTransition;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`modal-overlay ${overlayClassName}`}
          variants={modalOverlayVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--z-modal-backdrop, 900)',
          } as React.CSSProperties}
        >
          <motion.div
            className={`modal-content ${className}`}
            variants={modalContentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            style={style}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AnimatedModal;
